import { z } from "zod";

// Treasury (tesorería) reads — kill-hasura #56. Every sub-resource is deeply
// nested (rel_buyer, rel_supplier, rel_order, rel_bank, rel_user_open/close,
// collections, disbursement_payables, treasury_cash_desk_records...). The list
// endpoints stay lean and the detail (/:id) returns the full tree, exactly like
// purchases/orders. Permissive passthrough schemas keep every header scalar +
// the whole nested tree (whichever the endpoint returns) without stripping
// anything, so the same DTO flows to lean cards and to the detail-on-expand.
// Nested relation keys are EXACTLY the Hasura names (rel_*), the BFF mirrors
// them and already excludes `password`.

// Cobranzas (collections_deposits)
export const depositSchema = z.object({ id: z.string() }).passthrough();
export type DepositDTO = z.infer<typeof depositSchema>;

// Pagos (disbursements_disbursements)
export const disbursementSchema = z.object({ id: z.string() }).passthrough();
export type DisbursementDTO = z.infer<typeof disbursementSchema>;

// Cheques (treasury_treasury_checks) — the most cross-coupled (rel_deposit,
// rel_disbursement, rel_order, rel_expense, rel_bank).
export const checkSchema = z.object({ id: z.string() }).passthrough();
export type CheckDTO = z.infer<typeof checkSchema>;

// Caja (treasury_treasury_cash_desk) — un-paginated, records embedded
// (treasury_cash_desk_records), ordered closedAt DESC.
export const cashDeskSchema = z.object({ id: z.string() }).passthrough();
export type CashDeskDTO = z.infer<typeof cashDeskSchema>;

// Gastos (treasury_treasury_expenses)
export const expenseSchema = z.object({ id: z.string() }).passthrough();
export type ExpenseDTO = z.infer<typeof expenseSchema>;

// ───────────────────────── Cartera de cheques (trd-bff#114) ─────────────────
// A diferencia de los de arriba, estos schemas son ESTRICTOS y no passthrough:
// los de arriba espejan árboles de Hasura que no controlamos, mientras que este
// es un contrato nuevo, nuestro, y de números contables. Si el BFF cambia una
// key o manda un string donde va un número, queremos enterarnos en el parseo y
// no descubrirlo con un total en NaN en pantalla.

/** Con qué criterio el backend cortó los períodos. Va en toda respuesta. */
export const portfolioMetaSchema = z.object({
  timezone: z.string(),
  source: z.enum(["business_config", "default"]),
});
export type PortfolioMeta = z.infer<typeof portfolioMetaSchema>;

const withMeta = <T extends z.ZodType>(dataSchema: T) =>
  z.object({ data: dataSchema, meta: portfolioMetaSchema });

/** Un mes de la serie de STOCK. `month` es date-only (`YYYY-MM-DD`). */
export const portfolioMonthSchema = z.object({
  month: z.string(),
  count: z.number(),
  amount: z.number(),
});
export type PortfolioMonth = z.infer<typeof portfolioMonthSchema>;

export const portfolioMonthlyResponseSchema = withMeta(
  z.array(portfolioMonthSchema)
);

/**
 * Confianza de la fecha de salida — se muestra al usuario, no se esconde:
 * `abierto` sigue en cartera · `exacto` tiene deliveredAt real · `estimado` la
 * dedujimos de updatedAt.
 */
export const portfolioConfidenceSchema = z.enum([
  "abierto",
  "exacto",
  "estimado",
]);
export type PortfolioConfidence = z.infer<typeof portfolioConfidenceSchema>;

export const portfolioCheckSchema = z.object({
  id: z.string(),
  checkNumber: z.string().nullable(),
  rel_bank: z.object({ name: z.string().nullable() }).nullable(),
  amount: z.number(),
  receivedAt: z.string(),
  salio: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  dueDate: z.string().nullable(),
  posession: z.string(),
  status: z.string(),
  origin: z.string(),
  confidence: portfolioConfidenceSchema,
});
export type PortfolioCheck = z.infer<typeof portfolioCheckSchema>;

export const portfolioMonthDetailResponseSchema = withMeta(
  z.array(portfolioCheckSchema)
);

/** Modo RANGO: cheques distintos. `count`/`amount` son del set completo. */
export const portfolioRangeSchema = z.object({
  count: z.number(),
  amount: z.number(),
  checks: z.array(portfolioCheckSchema),
  /** La lista se recortó por el tope — el total sigue siendo verdadero. */
  truncated: z.boolean(),
});
export type PortfolioRange = z.infer<typeof portfolioRangeSchema>;

export const portfolioRangeResponseSchema = withMeta(portfolioRangeSchema);

const bucketSchema = z.object({ count: z.number(), amount: z.number() });

export const portfolioSummarySchema = z.object({
  inPortfolio: bucketSchema,
  overdue: bucketSchema,
  dueBuckets: z.object({
    upTo30: bucketSchema,
    from31To60: bucketSchema,
    over60: bucketSchema,
  }),
  largestAmount: z.number(),
  averageAmount: z.number(),
  averageDaysHeld: z.number(),
  /**
   * `reentryCount > 0` significa que aparecieron cheques que volvieron a
   * cartera: el modelo asume UN intervalo por cheque, así que la serie deja de
   * ser confiable. La UI lo muestra en vez de seguir dibujando números lindos.
   */
  integrityWarning: z.object({ reentryCount: z.number() }),
});
export type PortfolioSummary = z.infer<typeof portfolioSummarySchema>;

export const portfolioSummaryResponseSchema = withMeta(portfolioSummarySchema);

/** Un mes del flujo. Reconcilia: closing = opening + entered − exited. */
export const portfolioFlowSchema = z.object({
  month: z.string(),
  openingCount: z.number(),
  openingAmount: z.number(),
  enteredCount: z.number(),
  enteredAmount: z.number(),
  exitedCount: z.number(),
  exitedAmount: z.number(),
  closingCount: z.number(),
  closingAmount: z.number(),
});
export type PortfolioFlow = z.infer<typeof portfolioFlowSchema>;

export const portfolioFlowsResponseSchema = withMeta(
  z.array(portfolioFlowSchema)
);

export const portfolioByBankSchema = z.object({
  bankMdId: z.string().nullable(),
  bankName: z.string().nullable(),
  count: z.number(),
  amount: z.number(),
});
export type PortfolioByBank = z.infer<typeof portfolioByBankSchema>;

export const portfolioByBankResponseSchema = withMeta(
  z.array(portfolioByBankSchema)
);

export const portfolioByBuyerSchema = z.object({
  buyerId: z.string().nullable(),
  rel_buyer: z
    .object({
      name: z.string().nullable(),
      lastName: z.string().nullable(),
      nickname: z.string().nullable().optional(), // ADR-0005
    })
    .nullable(),
  count: z.number(),
  amount: z.number(),
});
export type PortfolioByBuyer = z.infer<typeof portfolioByBuyerSchema>;

export const portfolioByBuyerResponseSchema = withMeta(
  z.array(portfolioByBuyerSchema)
);

// Tipos de las respuestas completas (`{ data, meta }`). Se derivan del schema
// con `z.infer` para que agregar un campo al schema no requiera tocar el tipo.
export type PortfolioMonthlyResponse = z.infer<
  typeof portfolioMonthlyResponseSchema
>;
export type PortfolioMonthDetailResponse = z.infer<
  typeof portfolioMonthDetailResponseSchema
>;
export type PortfolioRangeResponse = z.infer<
  typeof portfolioRangeResponseSchema
>;
export type PortfolioSummaryResponse = z.infer<
  typeof portfolioSummaryResponseSchema
>;
export type PortfolioFlowsResponse = z.infer<
  typeof portfolioFlowsResponseSchema
>;
export type PortfolioByBankResponse = z.infer<
  typeof portfolioByBankResponseSchema
>;
export type PortfolioByBuyerResponse = z.infer<
  typeof portfolioByBuyerResponseSchema
>;
